/**
 * /agent/new-chat — minimal "creator" page.
 *
 * On mount: POST /v2/agent/chats (creates a fresh chat),
 * then navigate to /agent/{newId}. If creation fails, show
 * the error and a retry button.
 */

import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { agentApi } from '@/lib/agent.api';
import { getApiErrorMessage } from '@/lib/http';
import { agentSurfaceForPath } from '@/app/agent/surface';

export function AgentNewChatPage() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const surface = useMemo(() => agentSurfaceForPath(pathname), [pathname]);
    const [error, setError] = useState<string | null>(null);
    const [redirectTo, setRedirectTo] = useState<string | null>(null);
    const [retryToken, setRetryToken] = useState(0);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setError(null);
            try {
                const chat = await agentApi.create({
                    agentType: surface.agentType,
                });
                if (cancelled) return;
                setRedirectTo(`${surface.detailBase}/${chat._id}`);
            } catch (err) {
                if (cancelled) return;
                setError(getApiErrorMessage(err, 'Could not create a new search.'));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [retryToken, surface.agentType, surface.detailBase]);

    if (redirectTo) {
        return <Navigate to={redirectTo} replace />;
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                minHeight: '100%',
                gap: 16,
                color: 'var(--text-secondary)',
                fontFamily: 'var(--font-sans)',
                background: 'var(--surface-primary)',
            }}
        >
            {error ? (
                <>
                    <p style={{ fontSize: 14, color: 'var(--fg-danger)' }}>{error}</p>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            type="button"
                            onClick={() => setRetryToken((n) => n + 1)}
                            style={{
                                padding: '8px 14px',
                                background: 'var(--accent)',
                                color: '#FFFFFF',
                                border: 'none',
                                borderRadius: 9999,
                                fontSize: 13,
                                fontWeight: 500,
                                cursor: 'pointer',
                            }}
                        >
                            Retry
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(surface.listPath)}
                            style={{
                                padding: '8px 14px',
                                background: 'transparent',
                                color: 'var(--text-secondary)',
                                border: '1px solid var(--border-muted)',
                                borderRadius: 9999,
                                fontSize: 13,
                                cursor: 'pointer',
                            }}
                        >
                            Back to list
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <Loader2 size={20} strokeWidth={1.75} className="animate-spin" />
                    <p style={{ fontSize: 13 }}>Creating a new search…</p>
                </>
            )}
        </div>
    );
}
